import { STATEMENTS_STORAGE_URL, STORAGE_URL } from '@/constants'
import {
  EAutoPayType,
  EOrigin,
  EPaymentTypes,
  EUserType, IAutoPayItem, IAutoPaySetting,
  IFacility,
  IFacilityAdvancedSettings,
  IPayment,
  IResidentContact,
  ITransactionEffectiveDate,
  IUser
} from '@zunta/configurations'
import { ISelectItem } from '@/interfaces'
import moment from 'moment/moment'
import Vue from 'vue'

const phonePattern = /^\(\d{3}\) \d{3}-\d{4}$/
const zipPattern = /^\d{5}([-]\d{4})?$/
const einPattern = /^\d{2}-\d{7}$/
const websitePattern = /^(https?:\/\/)?(www\.)[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)|(https?:\/\/)?(www\.)?(?!ww)[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/
const ssnPattern = /^\d{3}-\d{2}-\d{4}$/

export function isSubset<T> (subset: T[], superset: T[]): boolean {
  const set = new Set(superset)
  return subset.every(a => set.has(a))
}

export function getStatementPathByKey (key: string, name: string) {
  return `${STATEMENTS_STORAGE_URL}/${name}?file=${key}`
}

export function isAddressValid (contact: IResidentContact) {
  return contact.zipCode && /^\d{5}(?:[-\s]\d{4})?$/.test(contact.zipCode) &&
    contact.state &&
    contact.city &&
    (contact.addressLine2 ||
      contact.addressLine1 ||
      contact.addressLine3)
}

export function isEmailValid (contact: IResidentContact) {
  return contact.email && /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/i.test(contact.email)
}

export function getFilePathByKey (key: string) {
  return `${STORAGE_URL}?file=${key}`
}

export function checkObjectForNullOrEmptyArray (theObject: object, key: string) {
  const objectValue = theObject[key as keyof object]
  return !objectValue || !(objectValue as []).length
}

export function validateZip (value: string) {
  if (!value) {
    return false
  }
  return zipPattern.test(value)
}

export function validateEIN (value: string) {
  if (!value) {
    return false
  }
  return einPattern.test(value)
}

export function validateWebsite (value: string) {
  if (!value) {
    return false
  }
  return websitePattern.test(value)
}

export function validateSSN (value: string) {
  if (!value) {
    return false
  }
  return ssnPattern.test(value)
}

export function validatePhone (value: string) {
  if (!value) {
    return false
  }
  return phonePattern.test(value)
}

export function validateEmail (email: string | undefined) {
  if (!email) return false
  const re = /^(([^<>()[\]\\.,;:\s@]+(\.[^<>()[\]\\.,;:\s@]+)*)|(.+))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(email)
}

export function validatePassword (password: string) {
  return password && password.length >= 8 && /[A-Z]+/.test(password) && /[a-z]+/.test(password)
}

export function validateDate (value: string | undefined): boolean {
  if (!value) {
    return false
  }
  const parts = value.split('/')
  if (parts.length !== 3) {
    return false
  }
  if (parseInt(parts[0], 10) > 12 || parseInt(parts[0], 10) <= 0) {
    return false
  }
  if (parseInt(parts[1], 10) > 31 || parseInt(parts[1], 10) <= 0) {
    return false
  }
  if (parts[2].length !== 4) {
    return false
  }
  return true
}

export function makeProcessingNotificationTimeout (component: Vue, message: string, timeout: number) {
  const theTimeout = setTimeout(() => {
    component.$eventBus.$emit('notify', {
      color: 'var( --status-info-color)',
      message: message,
      header: 'In Progress',
      icon: 'warning'
    })
  }, timeout)
  return theTimeout
}

export async function waitUntil (fn: Function, interval: number = 200) {
  return new Promise(async (resolve) => {
    let result = fn()
    if (result instanceof Promise) {
      result = await result
    }
    if (result) {
      resolve(null)
    } else {
      setTimeout(() => {
        waitUntil(fn, interval).then(resolve)
      }, interval)
    }
  })
}

export function getSearchOptions (search: string) {
  const params = new URLSearchParams(search)
  const entries = params.entries()
  const query: any = {}
  let item = entries.next()
  while (!item.done) {
    query[item.value[0]] = item.value[1]
    item = entries.next()
  }
  return query
}

export function callIfNothingSelected (fn: Function): any {
  return (...args: any[]) => {
    const callFn = () => {
      fn(...args)
    }
    setTimeout(() => {
      const selection: Selection | null = window.getSelection()
      console.log('selection', selection)
      if (!selection || selection.type !== 'Range') {
        callFn()
      }
    }, 100)
  }
}

export function copyText (text: string) {
  const input = document.createElement('input')
  input.value = text
  document.body.appendChild(input)
  input.select()
  input.setSelectionRange(0, 99999) /* For mobile devices */
  document.execCommand('copy')
  input.remove()
}

export function validateField (type: string, value: any, displayName: string) {
  const fieldsPatterns = {
    zip: {
      pattern: zipPattern,
      maxLength: 5
    },
    phone: {
      pattern: phonePattern
    },
    ein: {
      pattern: einPattern,
      maxLength: 10
    },
    website: {
      pattern: websitePattern,
      maxLength: 0
    },
    ssn: {
      pattern: ssnPattern,
      maxLength: 11
    },
    routingNumber: {
      pattern: /^\d{9,9}$/
    },
    accountNumber: {
      pattern: /\d{1,17}/
    }
  }
  if (type === 'date') {
    return validateDate(value) ? false : `${displayName} incorrect date`
  }
  // @ts-ignore
  const field = fieldsPatterns[type]
  if (!field) {
    return false
  }
  if (!field.pattern.test(value.toString())) {
    return `${displayName} field has incorrect format`
  } else if (field.maxLength && value.toString().length > field.maxLength) {
    return `${displayName} field too long`
  }
}

export function getPaidByLabel (payment: IPayment) {
  if (payment.paidBy && typeof payment.paidBy !== 'number') {
    if ((payment.paidBy as IUser).type === EUserType.RESIDENT) {
      return 'Resident Portal'
    }
    return `${payment.paidBy.firstName} ${payment.paidBy.lastName} ${payment.paymentType === EPaymentTypes.AUTO_PAYMENT ? ' (AP)' : ''}`
  }

  if (payment.paymentType === EPaymentTypes.AUTO_PAYMENT || payment.paymentType === EPaymentTypes.SCHEDULED_PAYMENT) {
    return 'Auto Payment'
  }

  return ''
}

export function getPaidByForDescription (payment: IPayment) {
  const label = getPaidByLabel(payment)
  return label ? `by ${label}` : null
}

export function polarToCartesian (centerX: number, centerY: number, radius: number, angleInDegrees: number) {
  const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0

  return {
    x: centerX + (radius * Math.cos(angleInRadians)),
    y: centerY + (radius * Math.sin(angleInRadians))
  }
}

export function describeArc (x: number, y: number, radius: number, startAngle: number, endAngle: number) {
  const start = polarToCartesian(x, y, radius, endAngle)
  const end = polarToCartesian(x, y, radius, startAngle)

  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1'

  return [
    'M', start.x, start.y,
    'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y
  ].join(' ')
}

export function formatTransactionEffectiveDate (transactionEffectiveDate: ITransactionEffectiveDate) {
  let month: any = transactionEffectiveDate.effectiveMonth
  const year = transactionEffectiveDate.effectiveYear
  if (!month && !year) {
    return 'Previous Balance'
  }
  if (month < 10) {
    month = `0${month}`
  }
  return `${month}/01/${year}`
}

export function getBatchCompleteLevel (batch: IFacilityAdvancedSettings, facilityOrigin: string, availableUsersIds: number[]) {
  if (!batch) {
    return 0
  }
  const requiredFields = [
    'name',
    'payers',
    'billingDay',
    'dueDate',
    'monthsBack',
    'billingContact',
    'reviewUser',
    'printedStatementUser',
    'zeroBalanceAction',
    'negativeBalanceAction'
  ]
  if (facilityOrigin === EOrigin.NCS) {
    requiredFields.push('ncsCollectionAction')
    requiredFields.push('ncsDirectDepositAction')
    if (batch.ncsDirectDepositAction === 'DO_NOT_INCLUDE_ON_STATEMENT') {
      requiredFields.push('ncsDirectDepositOption')
    }
  }
  if (batch.controlBalance) {
    requiredFields.push('controlBalanceAction')
  }
  let completed = 0
  requiredFields.forEach((key: string) => {
    if (key === 'payers') {
      if (batch.payers && batch.payers.length) {
        completed++
      }
    } else if (key === 'monthsBack') {
      if (batch.monthsBack !== null && batch.monthsBack !== undefined) {
        completed++
      }
    } else if (key === 'billingContact') {
      if (batch.billingContact && availableUsersIds.includes(batch.billingContact)) {
        completed++
      }
    } else if (key === 'reviewUser') {
      if (batch.reviewUser && availableUsersIds.includes(batch.reviewUser)) {
        completed++
      }
    } else if (key === 'printedStatementUser') {
      if (batch.printedStatementUser && availableUsersIds.includes(batch.printedStatementUser)) {
        completed++
      }
    } else if ((batch as any)[key]) {
      completed++
    }
  })
  return Math.floor(completed / requiredFields.length * 100)
}

export function plus (num1: any, num2: any) {
  return getDecNumber(getDecNumber(num1) + getDecNumber(num2))
}

export function minus (num1: any, num2: any) {
  return getDecNumber(getDecNumber(num1) - getDecNumber(num2))
}

export function getDecNumber (num: number) {
  if (isNaN(num)) {
    return 0
  }
  const p = 100
  return Math.round(num * p) / p
}

export function formatResidentAddress (mailContact: IResidentContact) {
  if (!mailContact) {
    return ''
  }
  const addressFields = [
    'addressLine1',
    'addressLine2',
    'addressLine3',
    'city',
    'state',
    'zipCode'
  ]
  let result = ''
  for (const key of addressFields) {
    // @ts-ignore
    if (!mailContact[key] || !mailContact[key].trim() || mailContact[key].trim() === '-1') {
      continue
    }
    // @ts-ignore
    result += mailContact[key].trim()
    if (key === 'city') {
      result += ','
    }
    result += ' '
  }
  return result
}

export function formatTypesOfContact (mailContact: IResidentContact) {
  if (!mailContact || !mailContact.typesOfContact) {
    return ''
  }
  return mailContact.typesOfContact.filter(t => t && t.trim()).join(', ')
}

export function openFile (src: any) {
  const _window = window.open(src, '_blank') || window
  _window.document.close()
  _window.document.execCommand('SaveAs', true, 'file.pdf')
}

export function extractDecimalsFromNumber (
  value: string,
  precision = 2,
  decimalSeparatorSymbol = '.'
): string {
  if (value === '0') return '0.00'

  let splitted = value.split('').filter(el => el !== '-')
  const isContainingMinus = (value.length - splitted.length) % 2 !== 0

  // while first element is 0 -> removing
  while (splitted[0] === '0') {
    splitted.splice(0, 1)
  }

  // all digits without a dot and coma
  const purifiedNumber = splitted.filter(el => el !== '.' && el !== ',')

  let numbersToReturn
  if (purifiedNumber.length <= precision) {
    // Calculating zeros to concatenate in front of the numbers
    const zerosMap = new Array(precision - (purifiedNumber.length - 1))
    zerosMap.fill('0')
    numbersToReturn = [...zerosMap, ...purifiedNumber]
  } else {
    numbersToReturn = purifiedNumber
  }

  numbersToReturn = isContainingMinus ? ['-', ...numbersToReturn] : numbersToReturn
  // Adding a decimal separator in the "precision" destination
  numbersToReturn.splice(numbersToReturn.length - precision, 0, decimalSeparatorSymbol)
  return numbersToReturn.join('')
}

export function getSelectContentLabel (value: any, obj: ISelectItem[], allValues: boolean = false) {
  if (!obj || !obj.length) {
    return ''
  }

  if (!value && value !== 0) {
    return ''
  }

  if (Array.isArray(value)) {
    // @ts-ignore
    const items = obj.filter((item: ISelectItem) => value.includes(item.value))
    if (!items || !items.length) {
      return ''
    }
    if (allValues) {
      return items.map((item: ISelectItem) => item.content).join('<br>')
    }
    return items.length + ' selected'
  }

  const item = obj.find((it: ISelectItem) => it.value === value)
  if (!item) {
    return value
  }
  return item.content
}

export function getTooltipListText (options: any[], key?: string): string {
  if (!options || !options.length) {
    return ''
  }
  let text = '<ul class="tooltip-list">'
  options.forEach((option: any) => {
    text += `<li class="tooltip-item">• ${key ? option ? option[key] : '' : option}</li>`
  })
  text += '</ul>'
  return text
}

export function getAutopayTooltipText (item: IAutoPayItem) {
  let text = 'Payment amount can change based on the resident\'s balance at the time that the autopay is charged.'
  const plan = item.setting as IAutoPaySetting
  // ts-ignore
  const itemDate = moment(item.initialPaymentDate).format('MMMM YYYY')
  if (plan.autoPayType === EAutoPayType.BILL_FULL_DUE && plan.includePrebillCharges) {
    return `Amount includes prebill charges for the month of ${itemDate}`
  }
  if (plan.autoPayType === EAutoPayType.BILL_FULL_DUE || plan.autoPayType === EAutoPayType.BILL_MONTH_DUE) {
    return `Amount is for charges for the month of ${itemDate}`
  }
  return text
}

export function spliceArray<T> (
  deleteCount: number,
  array: Array<T>,
  splice: boolean = true
) {
  if (!splice || !array.length) return array
  return [ ...array ].splice(0, deleteCount)
}

export function isProductionEnv () {
  // TODO change to app after testing
  return window.location.host === 'app.zunta.com' || window.location.host === 'release.zunta.com'
}

export function formatMoney (num: number | string, symbols: boolean = true, withLastPart: boolean = true): string {
  if (!num || isNaN(parseFloat(num as string))) {
    return (symbols ? '$' : '') + '0.00'
  }
  let money = parseFloat(num.toString())
  const sign = money < 0 ? '-' : ''
  money = Math.abs(money)
  // tslint:disable-next-line:no-shadowed-variable
  const fillZeros = (num: number, count: number) => {
    if (num < 10) {
      return count === 2 ? `00${num}` : `0${num}`
    }
    if (num < 100 && count === 2) {
      return `0${num}`
    }
    return num
  }
  const stringMoney = money.toFixed(2)
  const isHaveCents = stringMoney.indexOf('.') !== -1
  const lastPart = isHaveCents ? stringMoney.substr(stringMoney.indexOf('.') + 1) : '00'
  const secondPart = parseInt((money % 1000).toString())
  const firstPart = parseInt((money / 1000).toString())
  return `${sign}${symbols ? '$' : ''}${firstPart === 0 ? '' : formatMoney(firstPart, false, false) + ','}${firstPart === 0 ? secondPart : fillZeros(secondPart, 2)}${withLastPart ? (lastPart.length === 1 ? '.' + lastPart + '0' : '.' + lastPart) : ''}`
}
